import React from 'react';
import { Alert, Button, Modal, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import Swal from 'sweetalert2';
import moment from 'moment';
import { PageStatus } from 'enums';
import { Show } from 'Layout';
import { SamplesAPI } from '../../API';
import { Form as SampleForm } from '../Samples/Form';

type PanelCountry = {
  shortName: string;
  countryName: string;
  childSurveyId?: string;
};

type SampleRow = {
  id: string;
  name: string;
  profileCount: number;
  isActive: boolean;
  createdAt: string;
  // How many surveys link this sample (clones copy links); Delete on a shared
  // sample only unlinks it from this market.
  linkedSurveyCount?: number;
};

type Props = {
  parentSurveyId: string;
  panelCountries: PanelCountry[];
};

export const SAMPLES_PAGE_SIZE = 10;

type State = {
  status: string;
  error: string | null;
  samples: SampleRow[];
  page: number;
  totalCount: number;
  totalPage: number;
  // null = picker closed, PanelCountry = country chosen, ready to open form
  selectedCountry: PanelCountry | null;
  showPicker: boolean;
  showCreate: boolean;
  downloading: string | null;
  // upload flow
  showUploadPicker: boolean;
  uploadCountry: PanelCountry | null;
  showUploadModal: boolean;
  selectedFile: File | null;
  uploadName: string;
  uploadDescription: string;
  uploadLoading: boolean;
};

export class SurveySamplesTab extends React.Component<Props, State> {
  private _isMounted = false;
  private _request = 0;

  constructor(props: Props) {
    super(props);
    this.state = {
      status: PageStatus.None,
      error: null,
      samples: [],
      page: 1,
      totalCount: 0,
      totalPage: 0,
      selectedCountry: null,
      showPicker: false,
      showCreate: false,
      downloading: null,
      showUploadPicker: false,
      uploadCountry: null,
      showUploadModal: false,
      selectedFile: null,
      uploadName: '',
      uploadDescription: '',
      uploadLoading: false,
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchSamples(1);
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  componentDidUpdate(prevProps: Props) {
    // Re-fetch only when the listed market changes (e.g. its child gets saved
    // for the first time). Form passes a new panelCountries array on every
    // render, so comparing the array itself refetched on each re-render.
    if (this.marketKey(prevProps) !== this.marketKey(this.props)) {
      this.fetchSamples(1);
    }
  }

  // The market whose samples are listed: the first saved panel market. Every
  // caller passes one (the detail page's selected market, or the child a Form
  // was opened on).
  listMarket(props: Props = this.props): PanelCountry | undefined {
    return props.panelCountries.find((c) => !!c.childSurveyId);
  }

  marketKey(props: Props): string {
    return String(this.listMarket(props)?.childSurveyId || '');
  }

  // Only the samples linked to this market's child survey, one page at a time.
  fetchSamples(page = this.state.page) {
    const market = this.listMarket();
    const request = ++this._request;
    if (!market) {
      this.setState({ status: PageStatus.Loaded, samples: [], page: 1, totalCount: 0, totalPage: 0 });
      return;
    }
    this.setState({ status: PageStatus.Loading, error: null });
    SamplesAPI.getSurveySamples(market.childSurveyId as string, page, SAMPLES_PAGE_SIZE)
      .then(({ rows, totalCount, totalPage }) => {
        if (!this._isMounted || request !== this._request) return;
        // A delete can empty the last page: step back to the new last one.
        if (!rows.length && page > 1 && totalPage > 0) {
          this.fetchSamples(Math.min(page - 1, totalPage));
          return;
        }
        this.setState({ samples: rows, page, totalCount, totalPage, status: PageStatus.Loaded });
      })
      .catch((err: any) => {
        if (!this._isMounted || request !== this._request) return;
        this.setState({ status: PageStatus.Error, error: err.message || 'Could not load the samples.' });
      });
  }

  // Opens the create flow. If only one panel country has a saved child, go
  // straight to the form. If multiple have saved children, show the picker first.
  handleCreateClick = () => {
    const saved = this.props.panelCountries.filter((c) => !!c.childSurveyId);
    if (!saved.length) return; // button should be disabled in this case
    if (saved.length === 1) {
      this.setState({ selectedCountry: saved[0], showPicker: false, showCreate: true });
    } else {
      this.setState({ showPicker: true, showCreate: false, selectedCountry: null });
    }
  };

  handlePickerSelect = (option: any) => {
    const country = this.props.panelCountries.find((c) => c.shortName === option.value);
    if (country) {
      this.setState({ selectedCountry: country, showPicker: false, showCreate: true });
    }
  };

  handleCreateOverride = (payload: any): Promise<any> => {
    const { selectedCountry } = this.state;
    if (!selectedCountry?.childSurveyId) {
      return Promise.reject(new Error('No child survey selected'));
    }
    return SamplesAPI.createAndLink(
      payload,
      selectedCountry.childSurveyId,
      selectedCountry.shortName,
      selectedCountry.countryName,
    );
  };

  handleCreateSubmit = (_sampleId: string) => {
    this.setState({ showCreate: false, selectedCountry: null });
    this.fetchSamples(1);
  };

  // Every listed row is linked to this market. The unlink route deletes the
  // sample in country, unless other surveys also use it (then it only removes
  // this market's link), and refuses a sample this market doesn't link.
  handleDelete = (row: SampleRow) => {
    const market = this.listMarket();
    if (!market?.childSurveyId) return;
    const others = (row.linkedSurveyCount || 1) - 1;
    Swal.fire(others > 0 ? {
      title: 'Remove sample from this market?',
      text: `It is also used by ${others} other survey(s), so it is only removed from this market, not deleted.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Remove',
    } : {
      title: 'Delete sample?',
      text: 'This will remove the sample permanently.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Delete',
    }).then((result) => {
      if (!result.isConfirmed) return;
      SamplesAPI.unlinkSample(market.childSurveyId as string, row.id)
        .then(() => this.fetchSamples())
        .catch((err: any) => this.setState({ error: err.message }));
    });
  };

  handleDownload = (row: SampleRow) => {
    const sampleId = row.id;
    const market = this.listMarket();
    this.setState({ downloading: sampleId });
    // The export route is header-only on the backend, so pass the market's
    // country explicitly for the navbar-at-ALL case.
    SamplesAPI.getOneSampleUsersexportBlob(sampleId, { sourceCountryCode: market?.shortName, country: market?.countryName })
      .then((blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${sampleId}_Sample_Data.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((err: any) => this.setState({ error: err.message }))
      .finally(() => this.setState({ downloading: null }));
  };

  handleUploadClick = () => {
    const saved = this.props.panelCountries.filter((c) => !!c.childSurveyId);
    if (!saved.length) return;
    if (saved.length === 1) {
      this.setState({ uploadCountry: saved[0], showUploadPicker: false, showUploadModal: true, selectedFile: null, uploadName: '', uploadDescription: '' });
    } else {
      this.setState({ showUploadPicker: true, showUploadModal: false, uploadCountry: null });
    }
  };

  handleUploadPickerSelect = (option: any) => {
    const country = this.props.panelCountries.find((c) => c.shortName === option.value);
    if (country) {
      this.setState({ uploadCountry: country, showUploadPicker: false, showUploadModal: true, selectedFile: null, uploadName: '', uploadDescription: '' });
    }
  };

  convertCsvToBase64 = (file: File, name: string, description: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const csvContent = typeof reader.result === 'string' ? reader.result : '';
        if (!csvContent.trim()) { reject(new Error('CSV content is empty.')); return; }
        const reader2 = new FileReader();
        reader2.onload = (e: any) => {
          const base64Encoded = e.target.result.split(',')[1];
          resolve({ sampleName: name.trim(), sampleDescription: description.trim(), details: base64Encoded });
        };
        reader2.onerror = () => reject(new Error('FileReader encountered an error'));
        reader2.readAsDataURL(file);
      };
      reader.onerror = () => reject(new Error('FileReader encountered an error'));
      reader.readAsText(file);
    });
  };

  handleUpload = async () => {
    const { selectedFile, uploadName, uploadDescription, uploadCountry } = this.state;
    if (!selectedFile) { alert('Please select a file before uploading.'); return; }
    if (!uploadName.trim()) { alert('Please enter sample name first.'); return; }
    if (!uploadDescription.trim()) { alert('Please enter sample description first.'); return; }
    try {
      this.setState({ uploadLoading: true });
      const jsonData = await this.convertCsvToBase64(selectedFile, uploadName, uploadDescription);
      if (uploadCountry) {
        jsonData.sourceCountryCode = uploadCountry.shortName;
        jsonData.country = uploadCountry.countryName;
      }
      const sample: any = await SamplesAPI.uploadFileUniqueIds(jsonData);
      // Link it to the market so it shows in this list (same as Create Sample).
      if (sample?.id && uploadCountry?.childSurveyId) {
        await SamplesAPI.linkSample(uploadCountry.childSurveyId, sample.id);
      }
      alert('Sample created successfully!');
      this.setState({ showUploadModal: false, uploadCountry: null, selectedFile: null, uploadName: '', uploadDescription: '' });
      this.fetchSamples(1);
    } catch (error: any) {
      this.setState({ error: error.message });
    } finally {
      this.setState({ uploadLoading: false });
    }
  };

  render() {
    const { panelCountries } = this.props;
    const { status, error, samples, showPicker, showCreate, selectedCountry, downloading,
            showUploadPicker, showUploadModal, uploadCountry, selectedFile, uploadName, uploadDescription, uploadLoading,
            page, totalCount, totalPage } = this.state;

    const savedCountries = panelCountries.filter((c) => !!c.childSurveyId);
    const allUnsaved = savedCountries.length === 0;

    const pickerOptions = savedCountries.map((c) => ({
      label: `${c.countryName} (${c.shortName})`,
      value: c.shortName,
    }));

    return (
      <div className="p-3">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="mb-0 font-weight-bold">Samples</h6>
          <div className="d-flex">
            <Button
              variant="primary"
              size="sm"
              disabled={allUnsaved}
              title={allUnsaved ? 'Save the panel country details first to manage samples.' : undefined}
              onClick={this.handleCreateClick}
              className="mr-2"
            >
              <FontAwesomeIcon icon={['fas', 'plus']} className="mr-1" />
              Create Sample
            </Button>
            <Button
              variant="info"
              size="sm"
              disabled={allUnsaved}
              title={allUnsaved ? 'Save the panel country details first to manage samples.' : undefined}
              onClick={this.handleUploadClick}
            >
              <FontAwesomeIcon icon={['fas', 'upload']} className="mr-1" />
              Upload file
            </Button>
          </div>
        </div>

        <Alert
          variant="danger"
          show={!!error}
          onClose={() => this.setState({ error: null })}
          dismissible
        >
          {error}
        </Alert>

        {/* Country picker — shown when >1 saved panel countries exist */}
        {showPicker && (
          <div className="mb-3" style={{ maxWidth: 320 }}>
            <label className="small font-weight-bold mb-1">Select country for this sample:</label>
            <Select
              options={pickerOptions}
              onChange={this.handlePickerSelect}
              placeholder="Choose country..."
              autoFocus
            />
            <button
              type="button"
              className="btn btn-link btn-sm p-0 mt-1 text-muted"
              onClick={() => this.setState({ showPicker: false })}
            >
              Cancel
            </button>
          </div>
        )}

        {/* Country picker for upload — shown when >1 saved panel countries exist */}
        {showUploadPicker && (
          <div className="mb-3" style={{ maxWidth: 320 }}>
            <label className="small font-weight-bold mb-1">Select country for this upload:</label>
            <Select
              options={pickerOptions}
              onChange={this.handleUploadPickerSelect}
              placeholder="Choose country..."
              autoFocus
            />
            <button
              type="button"
              className="btn btn-link btn-sm p-0 mt-1 text-muted"
              onClick={() => this.setState({ showUploadPicker: false })}
            >
              Cancel
            </button>
          </div>
        )}

        {allUnsaved && (
          <Alert variant="info" className="py-2">
            Save the panel country details first to manage samples.
          </Alert>
        )}

        <Show when={status === PageStatus.Loading}>
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" variant="primary" size="sm" />
          </div>
        </Show>

        <Show when={status === PageStatus.Loaded}>
          <Show when={!samples.length && !allUnsaved}>
            <Alert variant="info" className="py-2">
              No samples for this market yet.{' '}
              <button type="button" className="btn btn-link p-0 align-baseline" onClick={this.handleCreateClick}>Create one.</button>
            </Alert>
          </Show>

          <Show when={!!samples.length}>
            <Table responsive size="sm" bordered>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Profile Count</th>
                  <th>Active</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {samples.map((row, index) => (
                  <tr key={row.id}>
                    <td>{(page - 1) * SAMPLES_PAGE_SIZE + index + 1}</td>
                    <td>{row.name}</td>
                    <td>{row.profileCount}</td>
                    <td>{row.isActive ? 'Yes' : 'No'}</td>
                    <td>{moment(row.createdAt).format('MM/DD/YYYY HH:mm A')}</td>
                    <td>
                      <div className="d-flex">
                        <button
                          type="button"
                          className="bg-transparent border-0 p-0 mr-3"
                          title="Download Excel"
                          disabled={downloading === row.id}
                          onClick={() => this.handleDownload(row)}
                        >
                          {downloading === row.id
                            ? <Spinner animation="border" size="sm" />
                            : <i className="fa fa-download text-success" />}
                        </button>
                        <button
                          type="button"
                          className="bg-transparent border-0 p-0"
                          title="Delete"
                          onClick={() => this.handleDelete(row)}
                        >
                          <i className="fa fa-trash text-danger" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {totalPage > 1 && (
              // type="button": this tab can sit inside Form's <form>.
              <div className="d-flex align-items-center justify-content-end small">
                <span className="mr-2 text-muted">Page {page} of {totalPage} · {totalCount} samples</span>
                <button type="button" className="btn btn-sm btn-outline-dark mr-1" disabled={page <= 1}
                  onClick={() => this.fetchSamples(page - 1)}>Previous</button>
                <button type="button" className="btn btn-sm btn-outline-dark" disabled={page >= totalPage}
                  onClick={() => this.fetchSamples(page + 1)}>Next</button>
              </div>
            )}
          </Show>
        </Show>

        {/* Create Sample modal — country locked via onCreateOverride */}
        {showCreate && selectedCountry && (
          <SampleForm
            show={showCreate}
            lockCountry={true}
            lockedCountry={{ label: selectedCountry.countryName, value: selectedCountry.shortName }}
            onClose={() => this.setState({ showCreate: false, selectedCountry: null })}
            onCreateOverride={this.handleCreateOverride}
            onSubmit={this.handleCreateSubmit}
          />
        )}

        {/* Upload file modal — zIndex above the survey Form modal (1201) */}
        <Modal
          show={showUploadModal}
          onHide={() => this.setState({ showUploadModal: false, uploadCountry: null })}
          centered
          style={{ zIndex: 1400 }}
          backdropClassName="upload-modal-backdrop"
        >
          <Modal.Header closeButton>
            <Modal.Title>Upload Sample File</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {uploadCountry && (
              <div className="mb-2">
                <span className="badge badge-primary" style={{ fontSize: '12px' }}>
                  {uploadCountry.countryName} ({uploadCountry.shortName})
                </span>
              </div>
            )}
            <div className="form-group">
              <label className="font-weight-bold">Sample Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={uploadName}
                onChange={(e) => this.setState({ uploadName: e.target.value })}
                placeholder="Enter sample name"
              />
            </div>
            <div className="form-group">
              <label className="font-weight-bold">Sample Description <span className="text-danger">*</span></label>
              <textarea
                className="form-control"
                value={uploadDescription}
                onChange={(e) => this.setState({ uploadDescription: e.target.value })}
                placeholder="Enter sample description"
                rows={3}
              />
            </div>
            <div className="form-group">
              <label className="font-weight-bold">File (CSV) <span className="text-danger">*</span></label>
              <input
                type="file"
                className="form-control-file"
                accept=".csv"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  if (file && file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
                    alert('Please upload a valid CSV file.');
                    this.setState({ selectedFile: null });
                    return;
                  }
                  this.setState({ selectedFile: file });
                }}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => this.setState({ showUploadModal: false, uploadCountry: null })}>
              Close
            </Button>
            <Button variant="primary" onClick={this.handleUpload} disabled={uploadLoading}>
              {uploadLoading ? <Spinner animation="border" size="sm" className="mr-1" /> : null}
              Upload
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
